import { useEffect } from 'react';
import PropTypes from 'prop-types';
import EverTrue from 'app';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';

/**
 * Dashboard key is `site-access`, a multivariate flag (`signal-only` /
 * `givingtree-only` / `both`) targeted per-org in LaunchDarkly. `useFeatureFlag`
 * returns the raw value under `flagEnabled` (the name is misleading — it does
 * not booleanize), so no conversion is needed here.
 */
const SITE_ACCESS_FLAG = 'siteAccess';

const SIGNAL_WEB_URL = {
  production: 'https://signal.evertrue.com',
  stage: 'https://stage-signal.evertrue.com',
};

/**
 * Sends the browser to evertrue-web (Signal) when the org's `site-access`
 * flag is `signal-only`. Cross-origin, so this uses `window.location.href`
 * rather than the Backbone router, which can only navigate within
 * givingtree-web. Both apps share routing in most cases, and each redirects
 * unknown paths to its own home page, so forwarding the current path is
 * safe even when it doesn't exist on the other side.
 */
const SiteAccessRedirect = ({ children }) => {
  const { flagEnabled: siteAccess } = useFeatureFlag(SITE_ACCESS_FLAG);
  const shouldRedirect = siteAccess === 'signal-only';

  useEffect(() => {
    if (shouldRedirect) {
      const base = EverTrue.config.environment === 'production' ? SIGNAL_WEB_URL.production : SIGNAL_WEB_URL.stage;
      const destination = new URL(`${window.location.pathname}${window.location.search}`, base);
      window.location.href = destination.toString();
    }
  }, [shouldRedirect]);

  if (shouldRedirect) {
    return null;
  }

  return <>{children}</>;
};

SiteAccessRedirect.propTypes = {
  children: PropTypes.node,
};

export default SiteAccessRedirect;
